import { supabase } from "@/integrations/supabase/client";
import { Conversation } from "@/pages/Inbox";
import { Tables } from "@/types/database.types";

// Define the detailed types for messages and form fields
interface FormField {
  field_id: string;
  title: string;
  value: string;
  field_type?: string; // Add field_type to distinguish between different types of fields
}

/**
 * Service for handling inbox-related operations
 */
export const inboxService = {
  /**
   * Fetches all conversations for the current user
   * Uses the get-inbox-conversations Edge Function
   */
  async getConversations(): Promise<{
    data: Conversation[] | null;
    error: Error | null;
  }> {
    try {
      // Get user session
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session?.user?.id) {
        return { data: null, error: new Error("No authenticated user found") };
      }

      const token = session.access_token;
      const userId = session.user.id;

      // Call the Edge Function
      const response = await fetch(
        `${
          import.meta.env.VITE_SUPABASE_URL
        }/functions/v1/get-inbox-conversations`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            userId,
            filterAnonymous: true, // Add parameter to filter anonymous users
          }),
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.error || `Server responded with status: ${response.status}`,
        );
      }

      let conversations = await response.json();

      // Filter out anonymous conversations (those not linked to an agent_chat_user)
      conversations = conversations.filter(
        (conv) =>
          conv.agent_chat_user_id && conv.agent_chat_user_id.trim() !== "",
      );

      return { data: conversations, error: null };
    } catch (error) {
      console.error("Error in getConversations:", error);
      return {
        data: null,
        error:
          error instanceof Error
            ? error
            : new Error("An unknown error occurred"),
      };
    }
  },

  /**
   * Fetches conversations for a specific agent
   * Uses the same Edge Function with a filter
   */
  async getAgentConversations(agentId: string): Promise<{
    data: Conversation[] | null;
    error: Error | null;
    count: number;
  }> {
    try {
      if (!agentId) {
        return {
          data: null,
          error: new Error("Agent ID is required"),
          count: 0,
        };
      }

      // Get user session
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session?.user?.id) {
        return {
          data: null,
          error: new Error("No authenticated user found"),
          count: 0,
        };
      }

      const token = session.access_token;
      const userId = session.user.id;

      // Call the Edge Function
      const response = await fetch(
        `${
          import.meta.env.VITE_SUPABASE_URL
        }/functions/v1/get-inbox-conversations`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            userId,
            agentId, // Add the agent ID to filter conversations
            filterAnonymous: true, // Add parameter to filter anonymous users
          }),
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.error || `Server responded with status: ${response.status}`,
        );
      }

      let conversations = await response.json();

      // Temporary debugging - log what we get from Edge Function
      if (conversations && conversations.length > 0) {
        console.log("🔍 InboxService Edge Function Response Debug:", {
          firstConversation: {
            user_email: conversations[0].user_email,
            user_phone: conversations[0].user_phone,
            hasUserEmail: !!conversations[0].user_email,
            hasUserPhone: !!conversations[0].user_phone,
            emailType: typeof conversations[0].user_email,
            phoneType: typeof conversations[0].user_phone,
            fullUserData: {
              user_name: conversations[0].user_name,
              user_email: conversations[0].user_email,
              user_phone: conversations[0].user_phone,
            },
          },
          totalConversations: conversations.length,
        });
      }

      // Filter out anonymous conversations (those not linked to an agent_chat_user)
      conversations = conversations.filter(
        (conv) =>
          conv.agent_chat_user_id && conv.agent_chat_user_id.trim() !== "",
      );

      return {
        data: conversations,
        error: null,
        count: conversations.length,
      };
    } catch (error) {
      console.error("Error in getAgentConversations:", error);
      return {
        data: null,
        error:
          error instanceof Error
            ? error
            : new Error("An unknown error occurred"),
        count: 0,
      };
    }
  },

  /**
   * Updates a conversation's state
   */
  async updateConversationState(
    conversationId: string,
    state: "open" | "completed",
  ): Promise<{ success: boolean; error: Error | null }> {
    try {
      const { error } = await supabase
        .from("conversations")
        .update({ state, updated_at: new Date().toISOString() })
        .eq("id", conversationId);

      if (error) throw error;
      return { success: true, error: null };
    } catch (error) {
      console.error("Error updating conversation state:", error);
      return {
        success: false,
        error:
          error instanceof Error
            ? error
            : new Error("An unknown error occurred"),
      };
    }
  },

  /**
   * Updates a conversation's event_scheduled status
   */
  async updateEventScheduled(
    conversationId: string,
    eventScheduled: boolean = true,
  ): Promise<{ success: boolean; error: Error | null }> {
    try {
      const { error } = await supabase
        .from("conversations")
        .update({
          event_scheduled: eventScheduled,
          updated_at: new Date().toISOString(),
        })
        .eq("id", conversationId);

      if (error) throw error;
      return { success: true, error: null };
    } catch (error) {
      console.error("Error updating event_scheduled status:", error);
      return {
        success: false,
        error:
          error instanceof Error
            ? error
            : new Error("An unknown error occurred"),
      };
    }
  },

  /**
   * Gets conversation details including messages and form fields
   */
  async getConversationDetails(conversationId: string): Promise<{
    data: {
      conversation: Tables<"conversations">;
      messages: Partial<Tables<"messages">>[];
      formFields: FormField[];
      agentInfo?: {
        agent_name?: string | null;
        avatar_url?: string | null;
        title?: string | null;
      };
      userInfo?: {
        name?: string | null;
        email?: string | null;
        phone?: string | null;
      };
    } | null;
    error: Error | null;
  }> {
    try {
      if (!conversationId) {
        return { data: null, error: new Error("Conversation ID is required") };
      }

      // Fetch conversation details
      const { data: convData, error: convError } = await supabase
        .from("conversations")
        .select("*")
        .eq("id", conversationId)
        .single();

      if (convError) throw convError;

      // Fetch messages
      const { data: messageData, error: msgError } = await supabase
        .from("messages")
        .select("*")
        .eq("conversation_id", conversationId)
        .order("created_at", { ascending: true });

      if (msgError) throw msgError;

      // Fetch form field values
      const { data: fieldValues, error: fieldError } = await supabase
        .from("conversation_field_values")
        .select(
          `
          id,
          value,
          field_id,
          form_fields(title, field_type)
        `,
        )
        .eq("conversation_id", conversationId);

      if (fieldError) throw fieldError;

      // Fetch agent details
      const { data: agentData, error: agentError } = await supabase
        .from("agents")
        .select("agent_name, avatar_url, title")
        .eq("id", convData.agent_id)
        .single();

      if (agentError) {
        console.error("Error fetching agent details:", agentError);
        // Continue without agent data
      }

      // Fetch user details if available (including pre_session_fields)
      let userData = null;
      let preSessionFields: FormField[] = [];
      if (convData.agent_chat_user_id) {
        const { data: userInfo, error: userError } = await supabase
          .from("agent_chat_users")
          .select("name, email, phone, pre_session_fields")
          .eq("id", convData.agent_chat_user_id)
          .single();

        if (!userError && userInfo) {
          userData = userInfo;

          // Process pre-session fields if they exist
          if (userInfo.pre_session_fields) {
            const preSessionData = userInfo.pre_session_fields as Record<
              string,
              any
            >;
            const fieldIds = Object.keys(preSessionData).filter(
              (key) => key !== "_timestamp",
            );

            // Fetch actual field titles from form_fields table
            if (fieldIds.length > 0) {
              const { data: formFieldsData, error: fieldsError } =
                await supabase
                  .from("form_fields")
                  .select("id, title")
                  .in("id", fieldIds);

              if (!fieldsError && formFieldsData) {
                // Create a map of field ID to title
                const fieldTitleMap = formFieldsData.reduce(
                  (acc, field) => {
                    acc[field.id] = field.title;
                    return acc;
                  },
                  {} as Record<string, string>,
                );

                // Process pre-session fields with actual titles
                preSessionFields = Object.entries(preSessionData)
                  .filter(
                    ([key, value]) =>
                      key !== "_timestamp" &&
                      value !== null &&
                      value !== undefined &&
                      value !== "",
                  )
                  .map(([key, value]) => ({
                    field_id: `pre_session_${key}`,
                    title:
                      fieldTitleMap[key] ||
                      key
                        .replace(/_/g, " ")
                        .replace(/\b\w/g, (l) => l.toUpperCase()), // Use actual title or fallback
                    value: String(value),
                    field_type: "pre-session",
                  }));
              } else {
                // Fallback to formatting field IDs if lookup fails
                preSessionFields = Object.entries(preSessionData)
                  .filter(
                    ([key, value]) =>
                      key !== "_timestamp" &&
                      value !== null &&
                      value !== undefined &&
                      value !== "",
                  )
                  .map(([key, value]) => ({
                    field_id: `pre_session_${key}`,
                    title: key
                      .replace(/_/g, " ")
                      .replace(/\b\w/g, (l) => l.toUpperCase()),
                    value: String(value),
                    field_type: "pre-session",
                  }));
              }
            }
          }
        }
      }

      // Format regular form fields
      const formattedFields = (fieldValues || []).map((fv) => ({
        field_id: fv.field_id || "",
        title: fv.form_fields?.title || "Unknown Field",
        value: fv.value || "",
        field_type: fv.form_fields?.field_type || "in_conversation",
      }));

      // Combine pre-session fields with regular form fields (pre-session fields first)
      const allFormFields = [...preSessionFields, ...formattedFields];

      return {
        data: {
          conversation: convData as Tables<"conversations">,
          // Use Partial to account for possible missing fields in the actual data
          messages: messageData || [],
          formFields: allFormFields,
          agentInfo: agentData || undefined,
          userInfo: userData
            ? {
                name: userData.name,
                email: userData.email,
                phone: userData.phone,
              }
            : undefined,
        },
        error: null,
      };
    } catch (error) {
      console.error("Error fetching conversation details:", error);
      return {
        data: null,
        error:
          error instanceof Error
            ? error
            : new Error("An unknown error occurred"),
      };
    }
  },

  /**
   * Filters conversations based on search term
   */
  filterConversations(
    conversations: Conversation[],
    searchTerm: string,
  ): Conversation[] {
    if (!searchTerm) return conversations;

    const term = searchTerm.toLowerCase();
    return conversations.filter(
      (conv) =>
        conv.agent_name?.toLowerCase().includes(term) ||
        conv.user_name?.toLowerCase().includes(term) ||
        conv.last_message?.toLowerCase().includes(term) ||
        Object.values(conv.form_field_values || {}).some(
          (value) =>
            typeof value === "string" && value.toLowerCase().includes(term),
        ),
    );
  },

  /**
   * Manually close a conversation and trigger form extraction (for testing)
   */
  async closeConversationManually(conversationId: string): Promise<{
    data: any | null;
    error: Error | null;
  }> {
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      if (!supabaseUrl) {
        throw new Error("Missing Supabase URL");
      }

      // Get the session for authentication
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) {
        throw new Error("User not authenticated");
      }

      // First, close the conversation
      const { error: updateError } = await supabase
        .from("conversations")
        .update({
          state: "completed",
          updated_at: new Date().toISOString(),
        })
        .eq("id", conversationId);

      if (updateError) {
        throw updateError;
      }

      // Then trigger the extract-form-fields function
      const response = await fetch(
        `${supabaseUrl}/functions/v1/extract-form-fields`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            conversationId: conversationId,
          }),
        },
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to trigger extraction: ${errorText}`);
      }

      const result = await response.json();

      return {
        data: result,
        error: null,
      };
    } catch (error) {
      console.error("Failed to close conversation manually:", error);
      return {
        data: null,
        error:
          error instanceof Error ? error : new Error("Unknown error occurred"),
      };
    }
  },
};
